
import React from 'react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Palette, Sun, Moon, Zap } from 'lucide-react';

export interface MindMapTheme {
  id: string;
  name: string;
  isDark: boolean;
  edgeType: 'smoothstep' | 'straight' | 'bezier';
  backgroundColor: string;
  nodeColors: string[];
  edgeColor: string;
}

const themes: MindMapTheme[] = [
  {
    id: 'light-smooth',
    name: 'Claro Suave',
    isDark: false,
    edgeType: 'smoothstep',
    backgroundColor: '#ffffff',
    nodeColors: ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'],
    edgeColor: '#6B7280'
  },
  {
    id: 'light-curve',
    name: 'Claro Curvo',
    isDark: false,
    edgeType: 'bezier',
    backgroundColor: '#ffffff',
    nodeColors: ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'],
    edgeColor: '#6B7280'
  },
  {
    id: 'dark-smooth',
    name: 'Escuro Suave',
    isDark: true,
    edgeType: 'smoothstep',
    backgroundColor: '#1f2937',
    nodeColors: ['#60A5FA', '#34D399', '#FBBF24', '#F87171', '#A78BFA'],
    edgeColor: '#9CA3AF'
  },
  {
    id: 'dark-curve',
    name: 'Escuro Curvo',
    isDark: true,
    edgeType: 'bezier',
    backgroundColor: '#1f2937',
    nodeColors: ['#60A5FA', '#34D399', '#FBBF24', '#F87171', '#A78BFA'],
    edgeColor: '#9CA3AF'
  },
  {
    id: 'wire-light',
    name: 'Fios Claro',
    isDark: false,
    edgeType: 'straight',
    backgroundColor: '#f8fafc',
    nodeColors: ['#2563EB', '#059669', '#D97706', '#DC2626', '#7C3AED'],
    edgeColor: '#374151'
  },
  {
    id: 'wire-dark',
    name: 'Fios Escuro',
    isDark: true,
    edgeType: 'straight',
    backgroundColor: '#0f172a',
    nodeColors: ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'],
    edgeColor: '#94A3B8'
  }
];

interface MindMapThemeSelectorProps {
  currentTheme: MindMapTheme;
  onThemeChange: (theme: MindMapTheme) => void;
}

export default function MindMapThemeSelector({ currentTheme, onThemeChange }: MindMapThemeSelectorProps) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="flex items-center gap-2">
          <Palette className="w-4 h-4" />
          Tema
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80">
        <div className="space-y-3">
          <h4 className="font-medium text-sm">Escolha um tema</h4>
          <div className="grid grid-cols-2 gap-2">
            {themes.map((theme) => (
              <button
                key={theme.id}
                onClick={() => onThemeChange(theme)}
                className={`p-3 rounded-lg border-2 transition-all duration-200 hover:shadow-md ${
                  currentTheme.id === theme.id 
                    ? 'border-blue-500 bg-blue-50' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center gap-2 mb-2">
                  {theme.isDark ? (
                    <Moon className="w-4 h-4" />
                  ) : (
                    <Sun className="w-4 h-4" />
                  )}
                  {theme.edgeType === 'straight' && (
                    <Zap className="w-4 h-4" />
                  )}
                  <span className="text-xs font-medium">{theme.name}</span>
                </div>
                <div 
                  className="w-full h-8 rounded mb-2"
                  style={{ backgroundColor: theme.backgroundColor }}
                >
                  <div className="flex h-full items-center justify-center space-x-1">
                    {theme.nodeColors.slice(0, 3).map((color, index) => (
                      <div
                        key={index}
                        className="w-2 h-2 rounded-full"
                        style={{ backgroundColor: color }}
                      />
                    ))}
                  </div>
                </div>
                <div className="flex items-center justify-center">
                  <div
                    className="w-8 h-0.5 rounded"
                    style={{ backgroundColor: theme.edgeColor }}
                  />
                </div>
              </button>
            ))}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export { themes };
